import { TopicArticles } from "@/components/site/ArticleTeasers";
import { createFileRoute, Link } from "@tanstack/react-router";
import { pageLinks } from "@/lib/locale-alternates";
import { ArrowRight, MessageCircle } from "lucide-react";
import { breadcrumb, faqPage, graph, service, webPage, TRAILS } from "@/lib/schema";
import { socialMeta, SERVICES_OG_IMAGE } from "@/lib/seo";
import { whatsappUrl } from "@/lib/whatsapp";
import { PageHero, SectionHeading } from "@/components/site/Blocks";
import { servicesFaqs } from "@/lib/services-faqs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/**
 * Canonical services headline. Rendered in the hero, the SEO title, the social
 * card title and the Service JSON-LD so all four can never drift apart.
 * Keep the ASCII ampersand and the exact wording — tests/services-heading.test.tsx
 * asserts it renders verbatim (including "&") in the page <h1>.
 */
export const SERVICES_HEADING =
  "SAP Consulting, Advisory & Resource Solutions";

/**
 * The <title> carries the positioning (10+ partner firms, 100+ specialists)
 * while the h1 and the social card keep the plain SERVICES_HEADING wording.
 */
const title = "SAP Services | 10+ Partner Firms, 100+ Specialists | OXORY";
const description =
  "SAP consulting, S/4HANA migration, SAP AMS and e-Fatura/e-invoicing support for enterprises in Europe and Türkiye — from 10+ partner firms and 100+ vetted SAP specialists.";

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      ...socialMeta({
        title,
        description,
        path: "/services",
        socialTitle: SERVICES_HEADING,
        image: SERVICES_OG_IMAGE,
        imageAlt: "OXORY consultants and partner specialists working on an enterprise SAP project in Europe",
      }),
    ],
    links: pageLinks("/services"),
    scripts: [
      graph(
        webPage({
          path: "/services",
          name: title,
          description,
          type: "CollectionPage",
          image: SERVICES_OG_IMAGE,
        }),
        breadcrumb(TRAILS["/services"]),
        service({
          path: "/services",
          name: SERVICES_HEADING,
          description,
          serviceType: "SAP Consulting, Advisory and Resource Solutions",
          offers: [
            "SAP Advisory with Freelance & Partner Experts",
            "SAP Project Support",
            "SAP Technology Consulting",
            "SAP EWM Consulting",
            "SAP TM Consulting",
            "Development Services",
            "Application Management Services",
            "Shoring: Nearshore SAP Delivery",
          ],
        }),
        ...(import.meta.env.SSR ? [faqPage(servicesFaqs)] : []),
      ),
    ],
  }),
  component: ServicesPage,
});

type SubItem = {
  id: string;
  name: string;
  lead: string;
  points: string[];
  link?: { to: string; label: string };
};

type Block = {
  id: string;
  eyebrow: string;
  title: string;
  lead: string;
  points?: string[];
  items?: SubItem[];
};

const blocks: Block[] = [
  {
    id: "recruitment",
    eyebrow: "Freelance & partner",
    title: "Advisory backed by freelance and partner experts",
    lead: "Every engagement is led by SAP consulting expertise and staffed from our network of vetted freelance and partner specialists — from a single expert to a full advisory and delivery team.",
    points: [
      "SAP functional, technical and architecture experts from freelance and partner networks",
      "Advisory teams and delivery-centre scale-ups for larger SAP programmes",
      "Technical screening and referencing before any expert reaches your shortlist",
      "Transparent rates, market feedback and realistic availability",
      "Access to consultants employed by OXORY partner firms, not only independent freelancers",
    ],
  },
  {
    id: "project-support",
    eyebrow: "SAP Project support",
    title: "When does an SAP project need extra support?",
    lead: "An SAP project needs extra support in its most critical phases, from final testing through go-live and into stable operations. OXORY adds that capacity exactly when it is needed.",
    points: [
      "Go-live support — extra capacity through SAP cutover and the final push before go-live",
      "Hypercare — close monitoring and fast fixes in the weeks right after SAP go-live",
      "After-go-live AMS — a structured handover into ongoing SAP application management",
      "Rollout, migration and upgrade support for planned SAP project phases",
      "Interim coverage for key SAP roles and hand-over periods",
    ],
  },
  {
    id: "technology-consulting",
    eyebrow: "SAP Consulting",
    title: "SAP Consulting",
    lead: "Platform-wide and module-specific SAP consulting in one place — from the technology layer underneath your processes to the logistics modules that run them.",
    items: [
      {
        id: "sap-consulting-general",
        name: "SAP Consulting — General",
        lead: "Advice and hands-on work on the SAP platform layer: architecture, integration, migration and performance underneath your business processes.",
        points: [
          "SAP landscape, platform and system architecture support",
          "Integration design across SAP and third-party systems",
          "Migration and conversion planning for SAP and modern ERP landscapes",
          "Technical reviews, assessments and second opinions for SAP projects",
        ],
        link: {
          to: "/services/technology-consulting",
          label: "Read the full SAP Technology Consulting overview",
        },
      },
      {
        id: "sap-ewm",
        name: "SAP EWM Consulting",
        lead: "Optimise your warehouse operations and increase efficiency with SAP Extended Warehouse Management.",
        points: [
          "Greenfield implementation, migration from WM and rollouts",
          "Inbound, outbound, internal movements and physical inventory design",
          "RF and packing process optimisation, labelling and automation interfaces",
        ],
      },
      {
        id: "sap-tm",
        name: "SAP TM Consulting",
        lead: "Bring your transportation processes into a single SAP Transportation Management system and increase efficiency.",
        points: [
          "Freight order, planning and carrier selection setup",
          "Freight cost calculation, settlement and forwarding processes",
          "Integration with EWM, ERP and external carrier platforms",
        ],
      },
    ],
  },
  {
    id: "development",
    eyebrow: "Development services",
    title: "Development Services",
    lead: "Advisory-led development capacity around SAP — delivered by individual experts or as a managed team with defined scope.",
    points: [
      "ABAP, backend and database platform development for SAP landscapes",
      "SAP Fiori, modern web applications and extensions",
      "Integration and interface development across SAP and third-party systems",
      "Custom code reviews, clean-core advisory and remediation before conversions",
    ],
  },
  {
    id: "ams",
    eyebrow: "AMS",
    title: "Application Management Services",
    lead: "Ongoing support for live SAP and IT landscapes with clear scope, responsibilities and escalation paths — advisory included, not just ticket handling.",
    points: [
      "Incident, problem and change handling with SAP expertise on call",
      "Functional and technical support across SAP modules and connected systems",
      "Small enhancements and release support",
      "Flexible service windows and defined reporting",
    ],
  },
  {
    id: "shoring",
    eyebrow: "Shoring",
    title: "Shoring: nearshore SAP consulting & delivery",
    lead: "European nearshore delivery for cost-efficient SAP capacity — advisory quality and proximity kept intact.",
    points: [
      "Nearshore SAP freelance and partner experts for dedicated project roles",
      "Managed nearshore teams for SAP development and AMS scope",
      "Overlapping time zones and direct communication with your advisory lead",
      "Single contract and single point of accountability",
    ],
  },
];

function ServicesPage() {
  return (
    <>
      <PageHero
        trail={TRAILS["/services"]}
        eyebrow="Our services"
        title={SERVICES_HEADING}
        intro="OXORY advises on SAP platforms, logistics modules and integration — and resources every engagement with vetted freelance and partner experts. Take the advisory, the delivery, or both as one accountable engagement."
      />

      <section className="border-b border-border bg-primary text-primary-foreground">
        <div className="section-shell py-12 md:py-16">
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <div className="max-w-2xl">
              <h2 className="text-2xl font-bold md:text-3xl">Request your SAP project</h2>
              <p className="mt-3 text-primary-foreground/90">
                Tell us what you need and we will match the right advisory, freelance and partner
                experts — from a single specialist to a full delivery team.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-md bg-primary-foreground px-5 py-3 text-sm font-bold text-primary hover:bg-primary-foreground/90"
              >
                Contact us <ArrowRight className="size-4" />
              </Link>
              <a
                href={whatsappUrl("/services", { utmContent: "services-cta" })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-primary-foreground/30 px-5 py-3 text-sm font-bold text-primary-foreground hover:bg-primary-foreground/10"
              >
                <MessageCircle className="size-4" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="section-shell py-20">
        <nav aria-label="Service overview" className="flex flex-wrap gap-2">
          {blocks.map((b) => (
            <a
              key={b.id}
              href={`#${b.id}`}
              className="rounded-md border border-border bg-card px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:text-foreground"
            >
              {b.eyebrow}
            </a>
          ))}
        </nav>

        <section aria-labelledby="services-topics" className="mt-12 max-w-3xl">
          <h2 id="services-topics" className="text-xl font-bold">
            SAP consulting, S/4HANA, AMS and e-Fatura from one partner
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Companies come to OXORY for <strong>SAP consulting</strong> in S/4HANA transformations,
            for <strong>SAP AMS</strong> (Application Management Services) after go-live and for
            e-invoicing mandates such as <strong>e-Fatura</strong> in Türkiye, XRechnung in Germany
            and Peppol across Europe. We staff each request with vetted SAP consultants — single
            experts, advisory teams or nearshore delivery.
          </p>
          <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm font-bold">
            <li><Link to="/services/technology-consulting" className="text-primary hover:underline">SAP technology consulting</Link></li>
            <li><Link to="/services/ams/what-is-sap-ams" className="text-primary hover:underline">What is SAP AMS?</Link></li>
            <li><Link to="/solutions/e-invoice" className="text-primary hover:underline">SAP e-invoicing and e-Fatura</Link></li>
            <li><Link to="/sap-projects-turkiye" className="text-primary hover:underline">SAP projects in Türkiye</Link></li>
          </ul>
        </section>

        <div className="mt-16 space-y-20">
          {blocks.map((b) => (
            <section key={b.id} id={b.id} className="scroll-mt-24">
              <SectionHeading eyebrow={b.eyebrow} title={b.title} lead={b.lead} />
              {b.points ? (
                <ul className="mt-8 grid gap-4 md:grid-cols-2">
                  {b.points.map((p) => (
                    <li
                      key={p}
                      className="rounded-lg border border-border bg-card p-5 text-sm leading-relaxed text-muted-foreground shadow-card"
                    >
                      {p}
                    </li>
                  ))}
                </ul>
              ) : null}
              {b.items ? (
                <div className="mt-8 grid gap-6 md:grid-cols-2">
                  {b.items.map((item) => (
                    <article
                      key={item.id}
                      id={item.id}
                      className="scroll-mt-24 rounded-lg border border-border bg-card p-6 shadow-card"
                    >
                      <h3 className="text-lg font-extrabold">{item.name}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {item.lead}
                      </p>
                      <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                        {item.points.map((p) => (
                          <li key={p} className="flex gap-2">
                            <span aria-hidden="true" className="text-primary">
                              —
                            </span>
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                      {item.link ? (
                        <Link
                          to={item.link.to}
                          className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-primary hover:gap-3"
                        >
                          {item.link.label} <ArrowRight className="size-4" />
                        </Link>
                      ) : null}
                    </article>
                  ))}
                </div>
              ) : null}
              {b.id === "recruitment" && (
                <Link
                  to="/for-specialists"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-primary hover:gap-3"
                >
                  Join our consultant network <ArrowRight className="size-4" />
                </Link>
              )}
              {b.id === "shoring" && (
                <Link
                  to="/shoring"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-primary hover:gap-3"
                >
                  Read the full Shoring overview <ArrowRight className="size-4" />
                </Link>
              )}
              {(b.id === "ams" || b.id === "project-support") && (
                <Link
                  to="/services/ams"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-primary hover:gap-3"
                >
                  Read the full AMS overview <ArrowRight className="size-4" />
                </Link>
              )}
            </section>
          ))}
        </div>

        <section className="mt-20">
          <SectionHeading
            eyebrow="FAQ: Frequently asked questions"
            title="Frequently asked questions about our SAP services"
          />
          <Accordion type="single" collapsible className="mt-10 max-w-3xl">
            {servicesFaqs.map(([q, a]) => (
              <AccordionItem key={q} value={q}>
                <AccordionTrigger className="text-left text-sm font-bold">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <div className="mt-20 rounded-lg border border-border bg-secondary p-10">
          <h2 className="text-2xl font-bold md:text-3xl">Not sure which model fits?</h2>
          <p className="mt-3 max-w-xl text-sm text-muted-foreground">
            Describe your SAP requirement and we will suggest whether an advisory engagement, a
            single freelance or partner expert, a managed team or a nearshore setup is the better
            route.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90"
            >
              Contact OXORY <ArrowRight className="size-4" />
            </Link>
            <Link
              to="/solutions/legal-tools"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-secondary"
            >
              Explore legal tools: e-invoicing &amp; Peppol <ArrowRight className="size-4" />
            </Link>
          </div>
        </div>
      </div>
    <TopicArticles topic="services-ams" locale="en" />
    </>
  );
}
